import { useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Warn before leaving setup with unsaved work. Copied per feature (see the
 * Events wizard's), with one addition: `allowNextLeave` lets the page's own
 * exits — Discard, or the redirect after saving — through without the guard
 * asking again. Changing `?step=` keeps the pathname, so step moves aren't held.
 */
export function useUnsavedChangesGuard(when: boolean) {
  const allowRef = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when &&
      !allowRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);

  useEffect(() => {
    if (!when && blocker.state === "blocked") blocker.reset();
  }, [when, blocker]);

  return {
    isBlocked: blocker.state === "blocked",
    confirmLeave: () => blocker.proceed?.(),
    cancelLeave: () => blocker.reset?.(),
    allowNextLeave: () => {
      allowRef.current = true;
    },
  };
}
